import { LineSeries, LineSvgLayer, ResponsiveLine } from "@nivo/line";
import {
    differenceInCalendarMonths,
    eachDayOfInterval,
    format,
    max,
    min,
    parseISO,
    startOfDay,
    Interval,
    eachMonthOfInterval,
    endOfMonth,
} from "date-fns";
import { CSSProperties, FC, useMemo, useState } from "react";
import { usePrint } from "app/hook/use-print";
import { ReportVulnerabilityListFieldsFragment, VulnerabilitySeverity } from "app/api/graph/types";
import { vulnerabilitySeverityColor, vulnerabilitySeverityLabel } from "app/api/graph/strings";
import { isBeforeOrEqual, isBetween } from "app/util/date";
import { notEmpty } from "app/util/array";
import { useChartTheme } from "app/hook/use-chart-theme";

type Props = {
    vulnerabilities: ReportVulnerabilityListFieldsFragment[];
    style?: CSSProperties;
    dateFrom?: Date;
    dateTo?: Date;
};

type Datum = {
    x: string;
    y: number;
};

type Item = {
    id: string;
    color: string;
    data: Datum[];
};

const margin = { top: 10, right: 10, bottom: 90, left: 40 };

enum TickPeriod {
    Day,
    Month,
}

const calculateDateInterval = (vulnerabilities: ReportVulnerabilityListFieldsFragment[]): Interval => {
    const dates = [
        ...vulnerabilities.map((vulnerability) => vulnerability.dateDiscovered),
        ...vulnerabilities.map((vulnerability) => vulnerability.dateResolved).filter(notEmpty),
    ].map((d) => startOfDay(parseISO(d)));

    return {
        start: min(dates),
        end: max(dates),
    };
};

const getDefaultTickPeriod = (vulnerabilities: ReportVulnerabilityListFieldsFragment[]): TickPeriod => {
    const interval = calculateDateInterval(vulnerabilities);
    return differenceInCalendarMonths(interval.end, interval.start) < 1 ? TickPeriod.Day : TickPeriod.Month;
};

const parseData = (vulnerabilities: ReportVulnerabilityListFieldsFragment[], tickPeriod: TickPeriod): Item[] => {
    const interval = calculateDateInterval(vulnerabilities);
    const range =
        tickPeriod === TickPeriod.Month
            ? eachMonthOfInterval(interval).map((date) => endOfMonth(date))
            : eachDayOfInterval(interval);
    return Object.values(VulnerabilitySeverity)
        .reverse()
        .map(
            (severity: VulnerabilitySeverity): Item => ({
                id: vulnerabilitySeverityLabel(severity),
                color: vulnerabilitySeverityColor(severity),
                data: range
                    .map(
                        (date): Datum => ({
                            x:
                                tickPeriod === TickPeriod.Month
                                    ? format(date, "yyyy-MM-01")
                                    : format(date, "yyyy-MM-dd"),
                            // Count the number of issues that were open on this date, for this severity
                            y: vulnerabilities
                                .filter((vulnerability) => vulnerability.severity === severity)
                                .filter((vulnerability) => {
                                    const dateDiscovered = startOfDay(parseISO(vulnerability.dateDiscovered));
                                    const dateClosedRaw = vulnerability.dateResolved ?? vulnerability.riskAcceptedAt;
                                    const dateClosed = dateClosedRaw ? startOfDay(parseISO(dateClosedRaw)) : undefined;
                                    // Open issue, count it towards every day since its discovery
                                    if (!dateClosed) {
                                        return isBeforeOrEqual(dateDiscovered, date);
                                    }
                                    // Issue was already resolved on (or before) its discovery date, so never count it
                                    if (isBeforeOrEqual(dateClosed, dateDiscovered)) {
                                        return false;
                                    }
                                    // Count the vulnerability on the dates that it was open
                                    return isBetween(date, dateDiscovered, dateClosed);
                                }).length,
                        }),
                    )
                    .filter(({ y }) => y > 0),
            }),
        );
};

const styleById: Record<string, CSSProperties> = {
    [vulnerabilitySeverityLabel(VulnerabilitySeverity.Critical)]: {
        strokeWidth: 5,
    },
    [vulnerabilitySeverityLabel(VulnerabilitySeverity.High)]: {
        strokeWidth: 4,
    },
    [vulnerabilitySeverityLabel(VulnerabilitySeverity.Medium)]: {
        strokeWidth: 3,
    },
    [vulnerabilitySeverityLabel(VulnerabilitySeverity.Low)]: {
        strokeWidth: 2,
    },
    default: {
        strokeWidth: 1,
    },
};

const customLines: LineSvgLayer<LineSeries> = ({ series, lineGenerator, xScale, yScale }) => {
    return series.map(({ id, data, color }) => (
        <path
            key={id}
            d={
                lineGenerator(
                    data.map((d) => ({
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        x: xScale(d.data.x ?? 0),
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        y: yScale(d.data.y ?? 0),
                    })),
                ) ?? undefined
            }
            fill="none"
            stroke={color}
            style={styleById[id] || styleById.default}
        />
    ));
};

export const ReportVulnerabilityTimelineChart: FC<Props> = ({ vulnerabilities, style, dateFrom, dateTo }) => {
    const theme = useChartTheme();
    const defaultTickPeriod = useMemo(() => getDefaultTickPeriod(vulnerabilities), [vulnerabilities]);
    const [tickPeriod, setTickPeriod] = useState<TickPeriod>(defaultTickPeriod);
    const data = useMemo(() => parseData(vulnerabilities, tickPeriod), [vulnerabilities, tickPeriod]);
    const { isPrint } = usePrint();

    return (
        <div style={{ height: "300px", ...style }} className="d-flex flex-column">
            <div className="text-end">
                <select value={tickPeriod} onChange={(e) => setTickPeriod(parseInt(e.target.value))}>
                    <option value={TickPeriod.Day}>Day</option>
                    <option value={TickPeriod.Month}>Month</option>
                </select>
            </div>
            <ResponsiveLine
                data={data}
                margin={margin}
                animate={!isPrint}
                colors={{ datum: "color" }}
                xFormat="time:%Y-%m-%d"
                xScale={{
                    format: "%Y-%m-%d",
                    precision: tickPeriod === TickPeriod.Month ? "month" : "day",
                    type: "time",
                    useUTC: false,
                    min: dateFrom !== undefined ? format(dateFrom, "yyyy-MM-dd") : undefined,
                    max: dateTo !== undefined ? format(dateTo, "yyyy-MM-dd") : undefined,
                }}
                yScale={{ type: "linear", min: 0, max: "auto", stacked: false, reverse: false }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickRotation: -45,
                    format: tickPeriod === TickPeriod.Month ? "%b" : "%b %d",
                    tickValues: 5,
                }}
                axisLeft={{
                    tickValues: 5,
                    format: (e: unknown) => (Number.isInteger(e) ? e : ""),
                }}
                gridYValues={5}
                pointSize={10}
                pointColor={{ theme: "background" }}
                pointBorderWidth={2}
                pointBorderColor={{ from: "serieColor" }}
                pointLabelYOffset={-12}
                useMesh={true}
                legends={[
                    {
                        anchor: "bottom",
                        direction: "row",
                        translateY: 90,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: "circle",
                    },
                ]}
                layers={[
                    "grid",
                    "markers",
                    "axes",
                    "areas",
                    "crosshair",
                    customLines,
                    "points",
                    "slices",
                    "mesh",
                    "legends",
                ]}
                theme={theme}
            />
        </div>
    );
};
